import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, Table, Button, Spinner, Alert, Modal, Form, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup, faPlus, faEdit, faTrash, faExternalLinkAlt, faSearch, faPlusCircle, faSync, faImage, faShareAlt } from '@fortawesome/free-solid-svg-icons';
import {
  getMyPlaylists,
  getMySongs,
  createPlaylist,
  updatePlaylist,
  deletePlaylist,
  getExplorePlaylistDetail,
  addSongToPlaylist,
  removeSongFromPlaylist,
  generatePlaylistThumbnail,
  applyPlaylistThumbnail
} from '../../services/exploreApi';
import { adminSyncAllTasks } from '../../services/songAdminApi';
import Pagination from '../common/Pagination';

const EMPTY_PLAYLIST = { title: '', slug: '', description: '', is_active: true, sort_order: 0 };
const ACCEPT_IMAGE = 'image/jpeg,image/png,image/jpg,image/gif,image/webp';
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

const ManageSongsModal = ({ show, playlist, onClose, prefilledTaskIds = [] }) => {
  const deviceId = useSelector((state) => state.auth.user?.device_id);
  const [detail, setDetail] = useState(null);
  const [mySongs, setMySongs] = useState([]);
  const [mySongsTotal, setMySongsTotal] = useState(0);
  const [detailLoading, setDetailLoading] = useState(false);
  const [mySongsLoading, setMySongsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [quickTaskId, setQuickTaskId] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [removingTaskId, setRemovingTaskId] = useState(null);
  const [message, setMessage] = useState(null);
  const [mySongsPage, setMySongsPage] = useState(1);
  const mySongsPageSize = 10;

  const fetchDetail = async () => {
    if (!playlist?.id) return;
    setDetailLoading(true);
    try {
      const res = await getExplorePlaylistDetail({ id: playlist.id, page: 1, items_per_page: 100 });
      setDetail(res.data?.data || null);
    } catch (err) {
      setMessage({ type: 'danger', text: err.response?.data?.message || err.message });
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchMySongs = async () => {
    setMySongsLoading(true);
    try {
      const params = { page: mySongsPage, items_per_page: mySongsPageSize };
      if (deviceId) params.device_id = deviceId;
      if (appliedSearch.trim()) params.search = appliedSearch.trim();
      const res = await getMySongs(params);
      setMySongs(res.data?.data?.items || []);
      setMySongsTotal(res.data?.data?.objects?.total ?? 0);
    } catch (err) {
      setMySongs([]);
      setMySongsTotal(0);
    } finally {
      setMySongsLoading(false);
    }
  };

  useEffect(() => {
    if (show && playlist?.id) {
      fetchDetail();
      setSearchTerm('');
      setAppliedSearch('');
      setMessage(null);
      setMySongsPage(1);
      setQuickTaskId(prefilledTaskIds.length > 0 ? prefilledTaskIds[0] : '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, playlist?.id]);

  useEffect(() => {
    if (show && playlist?.id) fetchMySongs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, playlist?.id, mySongsPage, appliedSearch]);

  const currentTaskIds = new Set((detail?.songs || []).map((s) => s.task_id));

  const handleAddByTaskId = async (e) => {
    e.preventDefault();
    if (!quickTaskId.trim()) return;
    setAddLoading(true);
    setMessage(null);
    try {
      const q = quickTaskId.trim();
      await addSongToPlaylist({ playlist_id: playlist.id, task_id: q });
      setMessage({ type: 'success', text: 'Song added' });
      setQuickTaskId('');
      fetchDetail();
    } catch (err) {
      setMessage({ type: 'danger', text: err.response?.data?.message || err.message });
    } finally {
      setAddLoading(false);
    }
  };

  const handleAddFromList = async (song) => {
    setAddLoading(true);
    setMessage(null);
    try {
      await addSongToPlaylist({ playlist_id: playlist.id, task_id: song.task_id });
      setMessage({ type: 'success', text: 'Song added' });
      fetchDetail();
    } catch (err) {
      setMessage({ type: 'danger', text: err.response?.data?.message || err.message });
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemove = async (taskIdToRemove) => {
    setRemovingTaskId(taskIdToRemove);
    try {
      await removeSongFromPlaylist({ playlist_id: playlist.id, task_id: taskIdToRemove });
      fetchDetail();
    } catch (err) {
      setMessage({ type: 'danger', text: err.response?.data?.message || err.message });
    } finally {
      setRemovingTaskId(null);
    }
  };

  const baseUrl = (process.env.REACT_APP_API_URL || window.location.origin + '/api').replace(/\/api\/?$/, '') || window.location.origin;
  const previewUrl = detail && `${baseUrl}/songs/explore/playlists/detail?id=${detail.id}`;

  if (!playlist) return null;

  return (
    <Modal show={show} onHide={onClose} size="lg" scrollable fullscreen="md-down">
      <Modal.Header closeButton>
        <Modal.Title>Manage songs – {playlist.title}</Modal.Title>
        {previewUrl && (
          <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary ms-2">
            <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />Preview
          </a>
        )}
      </Modal.Header>
      <Modal.Body>
        {message && <Alert variant={message.type} className="py-2" onClose={() => setMessage(null)} dismissible>{message.text}</Alert>}

        <div className="mb-4">
          <h6 className="mb-2">Current songs ({detail?.songs?.length ?? 0})</h6>
          {detailLoading ? <Spinner animation="border" size="sm" /> : (
            !detail?.songs?.length ? <p className="text-muted small mb-0">No songs yet.</p> : (
              <div className="border rounded p-2" style={{ maxHeight: 120, overflowY: 'auto' }}>
                {(detail.songs || []).map((s, idx) => (
                  <div key={s.task_id} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                    <span className="small">#{idx + 1} {s.title || s.task_id}</span>
                    <Button size="sm" variant="outline-danger" onClick={() => handleRemove(s.task_id)} disabled={removingTaskId === s.task_id}>
                      {removingTaskId === s.task_id ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faTrash} />}
                    </Button>
                  </div>
                ))}
              </div>
            )
          )}
        </div>

        <div className="mb-3">
          <h6 className="mb-2">Quick add (task_id)</h6>
          <Form onSubmit={handleAddByTaskId} className="d-flex gap-2">
            <Form.Control
              size="sm"
              className="form-control form-control-sm"
              value={quickTaskId}
              onChange={(e) => setQuickTaskId(e.target.value)}
              placeholder="Enter task_id..."
              disabled={addLoading}
            />
            <Button size="sm" type="submit" variant="primary" disabled={addLoading || !quickTaskId.trim()}>
              {addLoading ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faPlusCircle} />}
            </Button>
          </Form>
        </div>

        <div>
          <h6 className="mb-2">Pick from my songs</h6>
          <div className="d-flex gap-2 mb-2">
            <Form.Control
              size="sm"
              className="form-control form-control-sm flex-grow-1"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), setAppliedSearch(searchTerm), setMySongsPage(1))}
              placeholder="Search task_id, title..."
            />
            <Button size="sm" variant="outline-secondary" onClick={() => { setAppliedSearch(searchTerm); setMySongsPage(1); }}>
              <FontAwesomeIcon icon={faSearch} />
            </Button>
          </div>
          <div className="border rounded p-2" style={{ maxHeight: 220, overflowY: 'auto' }}>
            {mySongsLoading ? <div className="text-center py-3"><Spinner animation="border" size="sm" /></div> : (
              mySongs.length === 0 ? <p className="text-muted small mb-0">No songs found.</p> : (
                <>
                  {mySongs.map((s) => (
                    <div key={s.task_id} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <span className="small">{s.title || s.task_id} <code className="small">{s.task_id}</code></span>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => handleAddFromList(s)}
                        disabled={addLoading || currentTaskIds.has(s.task_id)}
                      >
                        {currentTaskIds.has(s.task_id) ? 'Added' : <FontAwesomeIcon icon={faPlusCircle} />}
                      </Button>
                    </div>
                  ))}
                  {mySongsTotal > mySongsPageSize && (
                    <div className="d-flex justify-content-center gap-1 mt-2">
                      <Button size="sm" variant="outline-secondary" disabled={mySongsPage <= 1} onClick={() => setMySongsPage((p) => p - 1)}>Prev</Button>
                      <span className="align-self-center small">Page {mySongsPage} / {Math.ceil(mySongsTotal / mySongsPageSize)}</span>
                      <Button size="sm" variant="outline-secondary" disabled={mySongsPage >= Math.ceil(mySongsTotal / mySongsPageSize)} onClick={() => setMySongsPage((p) => p + 1)}>Next</Button>
                    </div>
                  )}
                </>
              )
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};


const formatDate = (timestamp) => {
  if (!timestamp || timestamp === 0) return '—';
  return new Date(timestamp * 1000).toLocaleString();
};

const AdminPlaylistsList = () => {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [isActiveFilter, setIsActiveFilter] = useState('all');

  // Create/edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [formData, setFormData] = useState(EMPTY_PLAYLIST);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formMessage, setFormMessage] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Manage songs modal
  const [showManageModal, setShowManageModal] = useState(false);
  const [managingPlaylist, setManagingPlaylist] = useState(null);
  const [prefilledTaskIds, setPrefilledTaskIds] = useState([]);

  const baseUrl = (process.env.REACT_APP_API_URL || window.location.origin + '/api').replace(/\/api\/?$/, '') || window.location.origin;

  // Thumbnail modal
  const [showThumbnailModal, setShowThumbnailModal] = useState(false);
  const [thumbnailPlaylist, setThumbnailPlaylist] = useState(null);
  const [thumbnailPrompt, setThumbnailPrompt] = useState('');
  const [thumbnailLoading, setThumbnailLoading] = useState(false);
  const [thumbnailMessage, setThumbnailMessage] = useState(null);
  const [thumbnailPolling, setThumbnailPolling] = useState(false);
  const thumbnailPollRef = useRef(null);
  const thumbnailPlaylistIdRef = useRef(null);
  const thumbnailTaskIdRef = useRef(null);

  // Global sync
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);

  const openThumbnail = (p) => {
    setThumbnailPlaylist(p);
    setThumbnailPrompt('');
    setThumbnailMessage(null);
    setThumbnailPolling(false);
    if (thumbnailPollRef.current) clearInterval(thumbnailPollRef.current);
    setShowThumbnailModal(true);
  };

  const closeThumbnail = () => {
    if (thumbnailPollRef.current) clearInterval(thumbnailPollRef.current);
    setShowThumbnailModal(false);
    setThumbnailPlaylist(null);
    setThumbnailPolling(false);
  };

  const startThumbnailPolling = (playlistId, taskId) => {
    if (thumbnailPollRef.current) clearInterval(thumbnailPollRef.current);
    thumbnailPlaylistIdRef.current = playlistId;
    thumbnailTaskIdRef.current = taskId;
    thumbnailPollRef.current = setInterval(async () => {
      try {
        const res = await applyPlaylistThumbnail({ playlist_id: thumbnailPlaylistIdRef.current, task_id: thumbnailTaskIdRef.current });
        const state = res.data?.data?.state;
        if (state === 'success') {
          clearInterval(thumbnailPollRef.current);
          thumbnailPollRef.current = null;
          setThumbnailPolling(false);
          setThumbnailMessage({ type: 'success', text: 'Thumbnail generated successfully!' });
          fetchPlaylists();
        } else if (!res.data?.success || state === 'failed') {
          clearInterval(thumbnailPollRef.current);
          thumbnailPollRef.current = null;
          setThumbnailPolling(false);
          setThumbnailMessage({ type: 'danger', text: res.data?.message || 'Image generation failed.' });
        }
      } catch (err) {
        clearInterval(thumbnailPollRef.current);
        thumbnailPollRef.current = null;
        setThumbnailPolling(false);
        setThumbnailMessage({ type: 'danger', text: err.response?.data?.message || err.message });
      }
    }, 10000);
  };

  const handleGenerateThumbnail = async () => {
    if (!thumbnailPrompt.trim() || !thumbnailPlaylist) return;
    setThumbnailLoading(true);
    setThumbnailMessage(null);
    try {
      const res = await generatePlaylistThumbnail({ playlist_id: thumbnailPlaylist.id, prompt: thumbnailPrompt });
      const taskId = res.data?.data?.task_id;
      setThumbnailPolling(true);
      setThumbnailMessage({ type: 'info', text: 'Task submitted. Waiting for result...' });
      startThumbnailPolling(thumbnailPlaylist.id, taskId);
    } catch (err) {
      setThumbnailMessage({ type: 'danger', text: err.response?.data?.message || err.message });
    } finally {
      setThumbnailLoading(false);
    }
  };

  const fetchPlaylists = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, items_per_page: pageSize };
      if (isActiveFilter !== 'all') params.is_active = isActiveFilter === 'true';
      const res = await getMyPlaylists(params);
      setPlaylists(res.data?.data || []);
      const objs = res.data?.objects;
      setTotal(objs?.total ?? objs?.count ?? 0);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed');
      setPlaylists([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlaylists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, isActiveFilter]);

  const openCreate = () => {
    setEditingPlaylist(null);
    setFormData(EMPTY_PLAYLIST);
    setCoverImageFile(null);
    setFormMessage(null);
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditingPlaylist(p);
    setFormData({
      id: p.id,
      title: p.title || '',
      slug: p.slug || '',
      description: p.description || '',
      is_active: p.is_active !== false,
      sort_order: p.sort_order ?? 0
    });
    setCoverImageFile(null);
    setFormMessage(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.title?.trim()) return;
    if (coverImageFile && coverImageFile.size > MAX_IMAGE_SIZE) {
      setFormMessage({ type: 'danger', text: 'Image must be 5MB or less' });
      return;
    }
    setFormLoading(true);
    setFormMessage(null);
    try {
      const payload = { ...formData };
      if (coverImageFile) payload.cover_image = coverImageFile;
      if (editingPlaylist) {
        await updatePlaylist(payload);
        setFormMessage({ type: 'success', text: 'Playlist updated' });
      } else {
        await createPlaylist(payload);
        setFormMessage({ type: 'success', text: 'Playlist created' });
      }
      fetchPlaylists();
      setTimeout(() => setShowModal(false), 800);
    } catch (err) {
      setFormMessage({ type: 'danger', text: err.response?.data?.message || err.message });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete playlist "${p.title}"?`)) return;
    setDeletingId(p.id);
    try {
      await deletePlaylist({ id: p.id });
      fetchPlaylists();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const openManageSongs = (p, taskIds = []) => {
    setManagingPlaylist(p);
    setPrefilledTaskIds(taskIds);
    setShowManageModal(true);
  };

  const closeManageSongs = () => {
    setShowManageModal(false);
    setManagingPlaylist(null);
    setPrefilledTaskIds([]);
    fetchPlaylists();
  };

  const handleSyncAll = async () => {
    setSyncLoading(true);
    setSyncMessage(null);
    try {
      const res = await adminSyncAllTasks();
      const summary = res.data?.data?.summary || res.data?.summary;
      const synced = summary?.synced ?? 0;
      setSyncMessage({ type: 'success', text: `Sync complete — ${synced} song(s) synced to your library.` });
    } catch (err) {
      setSyncMessage({ type: 'danger', text: err.response?.data?.message || err.message || 'Sync failed' });
    } finally {
      setSyncLoading(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">
          <FontAwesomeIcon icon={faLayerGroup} className="me-2" />
          Admin Playlist
        </h2>
        <div className="d-flex gap-2">
          <Button variant="outline-success" onClick={handleSyncAll} disabled={syncLoading}>
            {syncLoading ? <Spinner animation="border" size="sm" className="me-1" /> : <FontAwesomeIcon icon={faSync} className="me-1" />}
            Sync My Songs
          </Button>
          <Button variant="primary" onClick={openCreate}>
            <FontAwesomeIcon icon={faPlus} className="me-1" />Create
          </Button>
        </div>
      </div>

      {syncMessage && <Alert variant={syncMessage.type} onClose={() => setSyncMessage(null)} dismissible>{syncMessage.text}</Alert>}
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

      <Card>
        <Card.Header className="d-flex gap-2 align-items-center">
          <Form.Select
            className="form-control form-select"
            style={{ width: 'auto' }}
            value={isActiveFilter}
            onChange={(e) => { setIsActiveFilter(e.target.value); setPage(1); }}
          >
            <option value="all">All</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </Form.Select>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : playlists.length === 0 ? (
            <p className="text-muted text-center py-4">No playlists found.</p>
          ) : (
            <>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Cover</th>
                    <th>Thumbnail</th>
                    <th>Title</th>
                    <th>Slug</th>
                    <th>Status</th>
                    <th>Songs</th>
                    <th>Sort</th>
                    <th>Created</th>
                    <th>Updated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {playlists.map((p) => (
                    <tr key={p.id}>
                      <td>{p.id}</td>
                      <td>
                        {p.cover_image_url ? (
                          <img src={p.cover_image_url} alt={p.title} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {p.thumbnail_url ? (
                          <img src={p.thumbnail_url} alt={`${p.title} thumbnail`} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>{p.title}</td>
                      <td><code className="small">{p.slug || '—'}</code></td>
                      <td>
                        <Badge bg={p.is_active ? 'success' : 'secondary'} className="text-white">
                          {p.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td>{p.songs_count ?? 0}</td>
                      <td>{p.sort_order ?? 0}</td>
                      <td>{formatDate(p.created_at)}</td>
                      <td>{formatDate(p.updated_at)}</td>
                      <td>
                        <div className="d-flex gap-1 flex-wrap">
                          <Button size="sm" variant="outline-primary" onClick={() => openManageSongs(p)}>
                            Manage Songs
                          </Button>
                          <Button size="sm" variant="outline-secondary" onClick={() => openEdit(p)} title="Edit">
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                          <Button size="sm" variant="outline-info" onClick={() => openThumbnail(p)} title="Generate Thumbnail">
                            <FontAwesomeIcon icon={faImage} />
                          </Button>
                          <Button size="sm" variant="outline-success" onClick={() => window.open(`${baseUrl}/songs/share/playlist/${p.slug}`, '_blank')} title="Share">
                            <FontAwesomeIcon icon={faShareAlt} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDelete(p)}
                            disabled={deletingId === p.id}
                            title="Delete"
                          >
                            {deletingId === p.id ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faTrash} />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Pagination currentPage={page} totalItems={total} itemsPerPage={pageSize} onPageChange={setPage} />
            </>
          )}
        </Card.Body>
      </Card>

      {/* Manage Songs Modal */}
      <ManageSongsModal
        show={showManageModal}
        playlist={managingPlaylist}
        onClose={closeManageSongs}
        prefilledTaskIds={prefilledTaskIds}
      />

      {/* Generate Thumbnail Modal */}
      <Modal show={showThumbnailModal} onHide={closeThumbnail} scrollable fullscreen="sm-down">
        <Modal.Header closeButton>
          <Modal.Title>Generate Thumbnail — {thumbnailPlaylist?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {thumbnailPlaylist?.thumbnail_url && (
            <div className="text-center mb-3">
              <p className="text-muted small mb-1">Current thumbnail</p>
              <img
                src={thumbnailPlaylist.thumbnail_url}
                alt="Current thumbnail"
                style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 6, objectFit: 'contain' }}
              />
            </div>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Prompt <span className="text-danger">*</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={thumbnailPrompt}
              onChange={(e) => setThumbnailPrompt(e.target.value)}
              placeholder="Describe the image to generate..."
              disabled={thumbnailPolling}
            />
          </Form.Group>
          {thumbnailPolling && (
            <div className="text-center mb-2">
              <Spinner animation="border" size="sm" className="me-2" />Generating...
            </div>
          )}
          {thumbnailMessage && <Alert variant={thumbnailMessage.type} className="mb-0 py-2">{thumbnailMessage.text}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeThumbnail}>
            {thumbnailPolling ? 'Cancel' : 'Close'}
          </Button>
          <Button variant="primary" onClick={handleGenerateThumbnail} disabled={thumbnailLoading || thumbnailPolling || !thumbnailPrompt.trim()}>
            {thumbnailLoading ? <Spinner animation="border" size="sm" /> : 'Generate'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create / Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} scrollable fullscreen="sm-down">
        <Modal.Header closeButton>
          <Modal.Title>{editingPlaylist ? 'Edit Playlist' : 'Create Playlist'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Title <span className="text-danger">*</span></Form.Label>
            <Form.Control
              className="form-control"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Playlist title"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Slug</Form.Label>
            <Form.Control
              className="form-control"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              placeholder="auto from title"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              className="form-control"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Thumbnail</Form.Label>
            <Form.Control
              type="file"
              className="form-control"
              accept={ACCEPT_IMAGE}
              onChange={(e) => setCoverImageFile(e.target.files?.[0] || null)}
            />
            <Form.Text className="text-muted">
              JPEG, PNG, JPG, GIF, WebP. Max 5MB.
              {editingPlaylist?.cover_image_url && !coverImageFile && ' To keep current image, do not select a new file.'}
            </Form.Text>
            {editingPlaylist?.cover_image_url && !coverImageFile && (
              <div className="mt-2">
                <img src={editingPlaylist.cover_image_url} alt="Current cover" style={{ maxHeight: 80, borderRadius: 4 }} />
              </div>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Sort order</Form.Label>
            <Form.Control
              type="number"
              className="form-control"
              value={formData.sort_order}
              onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value, 10) || 0 })}
            />
          </Form.Group>
          {formMessage && <Alert variant={formMessage.type} className="mt-2 mb-0 py-2">{formMessage.text}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={formLoading || !formData.title?.trim()}>
            {formLoading ? <Spinner animation="border" size="sm" /> : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const AdminPlaylists = () => <AdminPlaylistsList />;
export default AdminPlaylists;
